import { styled, useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";
import useScrollPosition from "@react-hook/window-scroll";
import React, { useCallback, useImperativeHandle, useRef } from "react";
import { useInView } from "react-intersection-observer";

import { ProductDataCancellationPolicy } from "../../apiHooks/useProduct";
import { MainColumnWrapper } from "../../components/MainColumnWrapper";
import { TOOLBAR_HEIGHT } from "../../types/HeaderBar";
import { ProductHeaderV2Props } from "./ProductHeaderV2";

export type HeaderSectionV2Props = Omit<ProductHeaderV2Props, "hasFreeCancellation"> & {
    withStickyAppBar: boolean;
    breadcrumbs?: React.ReactNode;
    showHeader?: boolean;
    cancellationPolicy?: ProductDataCancellationPolicy;
    ContentComponent: (props: HeaderSectionV2Props) => React.JSX.Element;
    productRatingComponent: React.ReactNode;
};

export const ProductHeaderSection = React.forwardRef(function HeaderSectionV2Component(
    props: HeaderSectionV2Props,
    forwardedRef
) {
    const { showHeader, withStickyAppBar, ContentComponent } = props;
    const scrollY = useScrollPosition();
    const isScrolled = scrollY > 0;
    const mainRef = useRef<Element | null>(null);
    const { ref: inViewRef, inView: isFullyVisible } = useInView({ threshold: 1 });
    const theme = useTheme();
    const isSticky = useMediaQuery(theme.breakpoints.up("sm"));

    useImperativeHandle(forwardedRef, () => mainRef.current);

    const setRefs = useCallback(
        (element: HTMLDivElement | null) => {
            mainRef.current = element;
            inViewRef(element);
        },
        [inViewRef]
    );

    if (!showHeader) return null;

    return (
        <HeaderSectionContainer
            ref={setRefs}
            data-is-sticky={isSticky}
            data-with-sticky-appbar={withStickyAppBar}
            data-is-fully-visible={isFullyVisible}
            data-is-scrolled={isScrolled}
            data-ref="product-header-section"
        >
            <HeaderSectionContentWrapper>
                <ContentComponent {...props} />
            </HeaderSectionContentWrapper>
        </HeaderSectionContainer>
    );
});

const HeaderSectionContainer = styled("div")(({ theme }) => ({
    padding: `${theme.spacing(2)} 0`,
    transition: theme.transitions.create("box-shadow", {
        duration: theme.transitions.duration.short,
    }),
    position: "static",
    top: 0,
    zIndex: 100,
    backgroundColor: theme.palette.common.white,
    '&[data-is-fully-visible="true"][data-is-scrolled="true"]': {
        boxShadow: "0 1px 15px rgba(0, 0, 0, 0.1)",
    },
    '&[data-with-sticky-appbar="true"]': {
        top: TOOLBAR_HEIGHT.sm,
        [theme.breakpoints.up("sm")]: {
            top: TOOLBAR_HEIGHT.md,
        },
    },
    "&[data-is-sticky='true']": {
        position: "sticky",
    },
}));

const HeaderSectionContentWrapper = styled(MainColumnWrapper)({
    position: "relative",
});
